import React, { useState, useEffect, useRef } from 'react'
import Sidebar from 'blocks.app/sidebar/sidebar'
import translate from 'core/translate'
import { api } from 'core/api/ConnectionManager'
import styles from './updateDevices__toolbar.jcss'
import { getURLSearchParamsByLocation } from 'features/routes/utils'
import { useLocation } from 'react-router'
import { DigitalSignage } from '../__updateQueue/UpdateQueue'
import { colors } from '../../../theme'
import moment from 'moment'
import helpers from '../../../core/helpers'
import { SidebarMultiselectMenu } from '../../../blocks.app/sidebarMultiselectMenu/SidebarMultiselectMenu'

type UpdateType = {
    updateType: string
    selectedItems?: IItem[]
}
interface IItem {
    name: string
    digitalSignage: { name: string }
    id: number
}
interface IUpdateDevicesToolbar {
    selectedItems: IItem[]
    onChange: (items: IItem[]) => void
    updateType: string
    selectedTask: DigitalSignage | null
    onCancelSelect: () => void
}

const getTitle = ({ updateType, selectedItems }: UpdateType) => {
    switch (updateType) {
        case 'devicesForUpdate':
            return `${translate('selectedDevicesForUpdate')} ${selectedItems?.length ?? ''}`
        default:
            return `${translate('selectedQueuesForUpdate')} ${selectedItems?.length ?? ''}`
    }
}

const getEmptyTitle = ({ updateType }: UpdateType) => {
    switch (updateType) {
        case 'devicesForUpdate':
            return translate('noDevicesSelected')
        default:
            return translate('noQueuesSelected')
    }
}

const MenuItem = (p_: { name: string; onClick: () => void }) => (
    <div onClick={p_.onClick} className={styles.menuItem}>
        {translate(p_.name)}
    </div>
)

const UpdateDevicesToolbar = (p_: IUpdateDevicesToolbar) => {
    const [selectedItems, setSelectedItems] = useState<IItem[]>([])
    const tasksIds = selectedItems.map((item: { id: number }) => item.id)
    const location = useLocation()
    const query = getURLSearchParamsByLocation(location)
    const updateType = query.updateType || 'devicesForUpdate'

    useEffect(() => {
        let selectedItems = p_.selectedItems

        if (updateType === 'tasksForUpdate') {
            selectedItems = selectedItems.map((item: IItem) => {
                if (item.hasOwnProperty('digitalSignage')) {
                    item.name = item.digitalSignage.name
                }

                return item
            })
        }

        setSelectedItems(selectedItems)
    }, [p_.selectedItems, updateType])

    function renderBottomContent() {
        if (updateType === 'devicesForUpdate') {
            return {
                right: null,
            }
        } else if (updateType === 'tasksForUpdate') {
            if (p_.selectedTask) {
                // @ts-ignore
                const backgroundColor = colors[p_.selectedTask.status]

                return {
                    right: (
                        <React.Fragment>
                            <div className={styles.device}>
                                <div className={styles.deviceTop} style={{ backgroundColor }}>
                                    {p_.selectedTask.lastScreenshotDate &&
                                        moment(p_.selectedTask.lastScreenshotDate).format('DD.MM.YYYY HH:mm:ss')}
                                </div>
                                <div
                                    className={styles.deviceCenter}
                                    style={{
                                        backgroundImage: p_.selectedTask.lastScreenshot
                                            ? `url(${p_.selectedTask.lastScreenshot})`
                                            : 'none',
                                        backgroundSize: 'contain',
                                    }}
                                />
                                <div className={styles.deviceBottom} style={{ backgroundColor }}></div>
                            </div>
                            <SidebarMultiselectMenu>
                                {['created', 'pending', 'in_progress'].includes(p_.selectedTask.task.status) && (
                                    <MenuItem
                                        name="cancelUpdateProcess"
                                        onClick={function () {
                                            if (p_.selectedTask && p_.selectedTask.task.id) {
                                                api.send('cancelDsUpdateTask', { id: [p_.selectedTask.task.id] })
                                            }
                                        }}
                                    />
                                )}
                                <MenuItem
                                    name="deleteUpdateTask"
                                    onClick={function () {
                                        if (p_.selectedTask && p_.selectedTask.task.id) {
                                            api.send('deleteDsUpdateTask', { id: [p_.selectedTask.task.id] }).then(
                                                () => {
                                                    p_.onCancelSelect()
                                                }
                                            )
                                        }
                                    }}
                                />
                            </SidebarMultiselectMenu>
                        </React.Fragment>
                    ),
                }
            }

            return {
                right: (
                    <React.Fragment>
                        <SidebarMultiselectMenu>
                            {selectedItems.some(
                                (item: any) => item.status !== 'success' && item.status !== 'canceled'
                            ) && (
                                <MenuItem
                                    name="cancelUpdateQueue"
                                    onClick={function () {
                                        api.send('cancelDsUpdateQueue', {
                                            queueIds: selectedItems
                                                .filter(
                                                    (item: any) =>
                                                        item.status !== 'canceled' && item.status !== 'success'
                                                )
                                                .map((item) => item.id),
                                        }).then(() => {
                                            p_.onChange([])
                                        })
                                    }}
                                />
                            )}
                            {selectedItems.some((item: any) => item.status === 'canceled') && (
                                <MenuItem
                                    name="resumeUpdateQueue"
                                    onClick={function () {
                                        api.send('resumeDsUpdateQueue', {
                                            queueIds: selectedItems
                                                .filter((item: any) => item.status === 'canceled')
                                                .map((item) => item.id),
                                        }).then(() => {
                                            p_.onChange([])
                                        })
                                    }}
                                />
                            )}
                            <MenuItem
                                name="deleteUpdateQueue"
                                onClick={function () {
                                    api.send('deleteDsUpdateQueue', {
                                        queueIds: selectedItems.map((item) => item.id),
                                    }).then(() => {
                                        p_.onChange([])
                                    })
                                }}
                            />
                        </SidebarMultiselectMenu>
                    </React.Fragment>
                ),
            }
        } else {
            return null
        }
    }

    return (
        <Sidebar
            selectedSide="right"
            onChange={function (list: IItem[]) {
                p_.onChange(list)
            }}
            topContent={renderBottomContent()}
            content={{
                right: {
                    title: p_.selectedTask ? p_.selectedTask.name : getTitle(p_),
                    options: !p_.selectedTask ? selectedItems : [],
                },
            }}
            selectedInfo={p_.selectedTask}
            isEmpty={{
                right: !selectedItems.length && !p_.selectedTask,
            }}
            emptyText={{
                right: {
                    title: getEmptyTitle(p_),
                    description: '',
                },
            }}
        />
    )
}

export default UpdateDevicesToolbar
